/**
 * As contas da ADN de seguro de vida — a planilha "ADN - NORTH" do Rodrigo
 * (reunião de 28/10/2026), fórmula por fórmula, para a ficha mostrar o capital
 * necessário enquanto a conversa acontece.
 *
 * Onde a planilha é diferente daqui, é de propósito:
 *   - "Doenças graves" no resumo da planilha é um número digitado (G99), não a
 *     conta da linha de cima; aqui é a conta.
 *   - Previdência fica fora da base do inventário. Na planilha a soma aponta
 *     para uma célula vazia (G66) e o valor da previdência nunca entra — o que
 *     por acaso está certo: VGBL/PGBL vão direto ao beneficiário, sem
 *     inventário.
 *   - Diferença negativa (já tem mais reservado do que precisa) vira zero no
 *     resumo: necessidade coberta não abate a dos outros itens.
 *
 * Idade é "ano de hoje − ano de nascimento", como a planilha (YEAR(NOW()) −
 * YEAR(nascimento)), para os números baterem com os que o Rodrigo já conhece.
 */

export interface DividaAdn {
  total?: number | null;
  parcela?: number | null;
}

export interface Adn {
  educacao?: { custo_mensal?: number | null; anos?: number | null }[];
  educacao_reserva?: number | null;
  gastos?: {
    fixos_casa?: number | null;
    fixos_saude?: number | null;
    outros?: number | null;
    lazer?: number | null;
  };
  /** Percentual (0–100) do gasto da casa que a renda do titular sustenta. */
  participacao_titular?: number | null;
  aposentadoria_titular?: number | null;
  aposentadoria_conjuge?: number | null;
  conjuge_nascimento?: string | null;
  reservado_invalidez?: number | null;
  reservado_doencas?: number | null;
  imoveis?: number | null;
  veiculos?: number | null;
  reserva_financeira?: number | null;
  dividas?: {
    auto?: DividaAdn;
    imobiliario?: DividaAdn;
    pessoais?: DividaAdn;
    outros?: DividaAdn;
  };
  renda_conjuge?: number | null;
}

export const CUSTO_DO_INVENTARIO = 0.15;
export const APOSENTADORIA_PADRAO = 65;

const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : Number(v) || 0);
const positivo = (v: number) => Math.max(0, v);

const anoDe = (data: unknown): number | null => {
  const m = /^(\d{4})-\d{2}-\d{2}/.exec(String(data ?? ""));
  return m ? Number(m[1]) : null;
};

export interface ResultadoAdn {
  educacao: { total: number; reserva: number; saldo: number };
  gastoMensal: number;
  participacaoTitular: number;
  invalidez: { titular: number | null; conjuge: number | null; total: number; reservado: number; diferenca: number };
  doencas: { titular: number; conjuge: number; total: number; reservado: number; diferenca: number };
  inventario: { base: number; custo: number };
  dividas: { total: number; parcelas: number };
  renda: { receita: number; despesas: number; saldo: number };
  necessidades: {
    educacao: number;
    invalidez: number;
    doencas: number;
    dividas: number;
    inventario: number;
    total: number;
  };
}

export const calcularAdn = (
  adn: Adn | undefined,
  pessoa: { nascimento?: string | null; renda?: number | null },
  hoje: Date = new Date(),
): ResultadoAdn => {
  const a = adn ?? {};
  const ano = hoje.getFullYear();

  const educacaoTotal = (a.educacao ?? []).reduce((s, e) => s + 12 * n(e?.custo_mensal) * n(e?.anos), 0);
  const educacaoReserva = n(a.educacao_reserva);

  const g = a.gastos ?? {};
  const gastoMensal = n(g.fixos_casa) + n(g.fixos_saude) + n(g.outros) + n(g.lazer);

  // Sem resposta, a renda do titular sustenta a casa inteira.
  const pct = a.participacao_titular == null || String(a.participacao_titular) === "" ? 100 : n(a.participacao_titular);
  const partTitular = Math.min(100, Math.max(0, pct)) / 100;
  const partConjuge = 1 - partTitular;

  const anoTitular = anoDe(pessoa.nascimento);
  const anoConjuge = anoDe(a.conjuge_nascimento);
  const apTitular = a.aposentadoria_titular == null ? APOSENTADORIA_PADRAO : n(a.aposentadoria_titular);
  const invalidezTitular =
    anoTitular == null ? null : positivo(apTitular - (ano - anoTitular)) * gastoMensal * partTitular * 12;
  const invalidezConjuge =
    anoConjuge == null || partConjuge === 0
      ? null
      : positivo((a.aposentadoria_conjuge == null ? APOSENTADORIA_PADRAO : n(a.aposentadoria_conjuge)) - (ano - anoConjuge)) *
        gastoMensal *
        partConjuge *
        12;
  const invalidezTotal = n(invalidezTitular) + n(invalidezConjuge);
  const reservadoInvalidez = n(a.reservado_invalidez);

  const doencasTitular = gastoMensal * partTitular * 24;
  const doencasConjuge = gastoMensal * partConjuge * 24;
  const doencasTotal = doencasTitular + doencasConjuge;
  const reservadoDoencas = n(a.reservado_doencas);

  const inventarioBase = n(a.imoveis) + n(a.veiculos) + n(a.reserva_financeira);
  const inventarioCusto = inventarioBase * CUSTO_DO_INVENTARIO;

  const d = a.dividas ?? {};
  const linhas = [d.auto, d.imobiliario, d.pessoais, d.outros];
  const dividasTotal = linhas.reduce((s, l) => s + n(l?.total), 0);
  const dividasParcelas = linhas.reduce((s, l) => s + n(l?.parcela), 0);

  const receita = n(pessoa.renda) + n(a.renda_conjuge);

  const necessidades = {
    educacao: positivo(educacaoTotal - educacaoReserva),
    invalidez: positivo(invalidezTotal - reservadoInvalidez),
    doencas: positivo(doencasTotal - reservadoDoencas),
    dividas: dividasTotal,
    inventario: inventarioCusto,
    total: 0,
  };
  necessidades.total =
    necessidades.educacao + necessidades.invalidez + necessidades.doencas + necessidades.dividas + necessidades.inventario;

  return {
    educacao: { total: educacaoTotal, reserva: educacaoReserva, saldo: educacaoTotal - educacaoReserva },
    gastoMensal,
    participacaoTitular: partTitular * 100,
    invalidez: {
      titular: invalidezTitular,
      conjuge: invalidezConjuge,
      total: invalidezTotal,
      reservado: reservadoInvalidez,
      diferenca: invalidezTotal - reservadoInvalidez,
    },
    doencas: {
      titular: doencasTitular,
      conjuge: doencasConjuge,
      total: doencasTotal,
      reservado: reservadoDoencas,
      diferenca: doencasTotal - reservadoDoencas,
    },
    inventario: { base: inventarioBase, custo: inventarioCusto },
    dividas: { total: dividasTotal, parcelas: dividasParcelas },
    renda: { receita, despesas: gastoMensal, saldo: receita - gastoMensal },
    necessidades,
  };
};

/** Idade como a planilha conta (diferença de anos). Vazio sem data. */
export const idadePelaPlanilha = (nascimento: unknown, hoje: Date = new Date()): number | null => {
  const a = anoDe(nascimento);
  return a == null ? null : hoje.getFullYear() - a;
};

/**
 * Aba "Cálculo" da planilha: quanto os valores do plano viram em N anos
 * corrigidos pelo IPCA. É a função FV do Excel com o sinal já invertido:
 *   - Pagamento: FV(ipca; anos; 12 × mensal; 0) — o que se terá pago, com a
 *     correção, pagando o mensal por `anos` anos (12 parcelas por ano).
 *   - Resgate e Patrimônio: FV(ipca; anos; 0; valor) — o valor de hoje
 *     corrigido até o fim do prazo.
 * `ipca` em fração ao ano (0,08 = 8%), como na planilha.
 */
export const IPCA_PADRAO = 0.08;

export interface ProjecaoIpca {
  anos: number;
  ipca: number;
  pagamento: number;
  resgate: number;
  patrimonio: number;
}

export const projetarPeloIpca = (p: {
  mensal?: number | null;
  anos?: number | null;
  ipca?: number | null;
  resgate?: number | null;
  patrimonio?: number | null;
}): ProjecaoIpca => {
  const anos = Math.max(0, n(p.anos));
  const ipca = p.ipca == null || String(p.ipca) === "" ? IPCA_PADRAO : n(p.ipca);
  const fator = Math.pow(1 + ipca, anos);
  const anual = 12 * n(p.mensal);
  const pagamento = ipca === 0 ? anual * anos : (anual * (fator - 1)) / ipca;
  return { anos, ipca, pagamento, resgate: n(p.resgate) * fator, patrimonio: n(p.patrimonio) * fator };
};
